const SENSITIVE_KEY_PATTERN = /password|token|secret|cookie|card|cpf|document|authorization|code/i
const TELEMETRY_SCHEMA = "2"
const PENDING_TELEMETRY_KEY = "__peterTelemetryPending"
const CUSTOM_TELEMETRY_EVENT = "peter:telemetry"

export function trackTelemetryEvent(type, details = {}) {
  if (typeof window === "undefined") return

  const event = { type: String(type || "").trim(), details }
  if (!event.type) return

  if (window.__peterTelemetryStarted) {
    window.dispatchEvent(new CustomEvent(CUSTOM_TELEMETRY_EVENT, { detail: event }))
    return
  }

  const pending = Array.isArray(window[PENDING_TELEMETRY_KEY]) ? window[PENDING_TELEMETRY_KEY] : []
  pending.push(event)
  window[PENDING_TELEMETRY_KEY] = pending.slice(-50)
}

export function startTelemetry({ apiBaseUrl, appSlug, appId, getToken = () => localStorage.getItem("token") }) {
  if (typeof window === "undefined" || window.__peterTelemetryStarted) return () => {}

  const normalizedSlug = String(appSlug || "").trim().toLowerCase()
  if (!normalizedSlug) return () => {}

  window.__peterTelemetryStarted = true

  const endpoint = `${String(apiBaseUrl).replace(/\/+$/, "")}/interactions/batch`
  const sessionKey = `peter_telemetry_session_${normalizedSlug}`
  const sessionId = sessionStorage.getItem(sessionKey) || createId()
  sessionStorage.setItem(sessionKey, sessionId)

  let queue = []
  let lastPath = window.location.pathname + window.location.search
  let scrollMilestones = new Set()
  let flushing = false
  let sessionEnded = false

  function createId() {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  function clean(value, limit = 200) {
    return String(value ?? "").replace(/\s+/g, " ").trim().slice(0, limit)
  }

  function cleanMetadataValue(value) {
    if (typeof value === "number") return Number.isFinite(value) ? value : null
    if (typeof value === "boolean") return value
    return clean(value, 500)
  }

  function page() {
    return window.location.pathname + window.location.search
  }

  function enqueue(type, details = {}) {
    const metadata = {}
    for (const [key, value] of Object.entries(details.metadata || {})) {
      if (!SENSITIVE_KEY_PATTERN.test(key) && value !== undefined && value !== null) {
        const cleaned = cleanMetadataValue(value)
        if (cleaned !== null) metadata[key] = cleaned
      }
    }

    queue.push({
      id: createId(),
      type: clean(type, 100),
      timestamp: new Date().toISOString(),
      page: page(),
      label: clean(details.label),
      target: clean(details.target),
      metadata,
    })

    if (queue.length >= 20) flush()
  }

  async function flush(force = false) {
    if ((!force && flushing) || !queue.length) return
    if (!force) flushing = true

    const events = queue.splice(0, 50)
    const token = getToken?.()

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        keepalive: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Peter-App": normalizedSlug,
          "X-App-Slug": normalizedSlug,
          "X-Telemetry-Schema": TELEMETRY_SCHEMA,
          "X-Frontend-Page": window.location.href,
          ...(appId ? { "X-App-ID": String(appId) } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId, events }),
      })

      if (response.status === 429 || response.status >= 500) {
        queue.unshift(...events.slice(-20))
      }
    } catch {
      queue.unshift(...events.slice(-20))
    } finally {
      if (!force) flushing = false
    }
  }

  function deferNavigation(source) {
    const callback = () => recordNavigation(source)
    if (typeof window.queueMicrotask === "function") window.queueMicrotask(callback)
    else window.setTimeout(callback, 0)
  }

  function recordNavigation(source) {
    const current = page()
    if (current === lastPath) return
    enqueue("navigation", { label: current, metadata: { from: lastPath, source } })
    lastPath = current
    scrollMilestones = new Set()
  }

  function onClick(event) {
    const element = event.target?.closest?.("a,button,[role='button'],[data-track]")
    if (!element || element.closest?.("[data-telemetry-ignore]")) return

    enqueue("click", {
      label: element.dataset?.track || element.getAttribute("aria-label") || element.textContent || element.name || element.id || element.tagName,
      target: element.getAttribute("href") || element.id || element.name || element.tagName,
      metadata: {
        tag: element.tagName,
        destination: element.getAttribute("href"),
      },
    })
  }

  function onSubmit(event) {
    const form = event.target
    if (form.closest?.("[data-telemetry-ignore]")) return

    const identity = form.getAttribute("aria-label") || form.name || form.id || "formulário"
    const searchForm = /search|busca|pesquisa/i.test(identity)
    enqueue(searchForm ? "search" : "form_submit", {
      label: identity,
      target: form.action || page(),
      metadata: { method: form.method || "GET" },
    })
  }

  function onChange(event) {
    const element = event.target
    if (!element?.matches?.("select,input[type='checkbox'],input[type='radio']") || element.closest?.("[data-telemetry-ignore]")) return

    enqueue(element.matches("select") ? "filter" : "field_change", {
      label: element.getAttribute("aria-label") || element.name || element.id || element.type,
      target: element.id || element.name || element.tagName,
      metadata: { control: element.type || element.tagName, checked: element.checked },
    })
  }

  function onScroll() {
    const documentHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1)
    const percentage = Math.min(100, Math.round((window.scrollY / documentHeight) * 100))

    for (const milestone of [25, 50, 75, 100]) {
      if (percentage >= milestone && !scrollMilestones.has(milestone)) {
        scrollMilestones.add(milestone)
        enqueue("scroll", { label: `${milestone}% da página`, metadata: { milestone } })
      }
    }
  }

  function onError(event) {
    enqueue("frontend_error", {
      label: event.message || "Erro JavaScript",
      metadata: { source: event.filename, line: event.lineno, column: event.colno },
    })
  }

  function onRejection(event) {
    enqueue("frontend_error", {
      label: event.reason?.message || "Promise rejeitada",
      metadata: { kind: "unhandledrejection" },
    })
  }

  function onCustomTelemetry(event) {
    const type = clean(event.detail?.type, 100)
    if (!type) return
    enqueue(type, event.detail?.details || {})
  }

  function onPopState() {
    recordNavigation("popstate")
  }

  function onPageHide() {
    if (!sessionEnded) {
      sessionEnded = true
      enqueue("session_end", { label: "Sessão encerrada" })
    }
    flush(true)
  }

  const originalPush = window.history.pushState
  const originalReplace = window.history.replaceState
  window.history.pushState = function (...args) {
    const result = originalPush.apply(this, args)
    deferNavigation("pushState")
    return result
  }
  window.history.replaceState = function (...args) {
    const result = originalReplace.apply(this, args)
    deferNavigation("replaceState")
    return result
  }

  document.addEventListener("click", onClick, true)
  document.addEventListener("submit", onSubmit, true)
  document.addEventListener("change", onChange, true)
  window.addEventListener(CUSTOM_TELEMETRY_EVENT, onCustomTelemetry)
  window.addEventListener("popstate", onPopState)
  window.addEventListener("error", onError)
  window.addEventListener("unhandledrejection", onRejection)
  window.addEventListener("scroll", onScroll, { passive: true })
  window.addEventListener("pagehide", onPageHide)

  const pendingTelemetry = Array.isArray(window[PENDING_TELEMETRY_KEY])
    ? window[PENDING_TELEMETRY_KEY].splice(0, 50)
    : []
  for (const pendingEvent of pendingTelemetry) onCustomTelemetry({ detail: pendingEvent })

  enqueue("session_start", {
    label: "Sessão iniciada",
    metadata: {
      referrer: document.referrer,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      telemetry_schema: TELEMETRY_SCHEMA,
    },
  })

  const timer = window.setInterval(flush, 5000)

  return () => {
    window.clearInterval(timer)
    onPageHide()
    document.removeEventListener("click", onClick, true)
    document.removeEventListener("submit", onSubmit, true)
    document.removeEventListener("change", onChange, true)
    window.removeEventListener(CUSTOM_TELEMETRY_EVENT, onCustomTelemetry)
    window.removeEventListener("popstate", onPopState)
    window.removeEventListener("error", onError)
    window.removeEventListener("unhandledrejection", onRejection)
    window.removeEventListener("scroll", onScroll)
    window.removeEventListener("pagehide", onPageHide)
    window.history.pushState = originalPush
    window.history.replaceState = originalReplace
    window.__peterTelemetryStarted = false
  }
}
